import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';

@Component({
    selector: 'app-message-log-dialog',
    templateUrl: './message-log-dialog.component.html'
})
export class MessageLogDialogComponent extends DialogContentBase {
    message: string = '';
    copied = false;

    constructor(
        override dialog: DialogRef,
        private sanitizer: DomSanitizer
    ) {
        super(dialog);
    }

    get highlightedMessage(): SafeHtml {
        const formatted = this.formatXml(this.message);

        const escaped = formatted
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const highlighted = escaped
            // Inline text content: <Tag>value</Tag>
            .replace(
                /(&gt;)([^&<][^<]*)(&lt;\/)/g,
                '$1<span style="color:#065f46">$2</span>$3'
            )
            // Attribute values
            .replace(/&quot;([^&]*)&quot;/g, '&quot;<span style="color:#d97706">$1</span>&quot;')
            // Attribute names
            .replace(/ ([\w:.-]+)=&quot;/g, ' <span style="color:#7c3aed">$1</span>=&quot;')
            // Closing tag names
            .replace(/&lt;\/([\w:.-]+)/g, '&lt;/<span style="color:#2563eb">$1</span>')
            // Opening tag names
            .replace(/&lt;([\w:.-]+)/g, '&lt;<span style="color:#2563eb">$1</span>')
            // Self-closing end
            .replace(/\/&gt;/g, '<span style="color:#9ca3af">/&gt;</span>');

        return this.sanitizer.bypassSecurityTrustHtml(highlighted);
    }

    async copyToClipboard() {
        await navigator.clipboard.writeText(this.message);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
    }

    close() {
        this.dialog.close();
    }

    private formatXml(xml: string): string {
        const lines = xml
            .replace(/>\s*</g, '><')
            .replace(/(<\/?[^>]+>)/g, '$1\n')
            .split('\n')
            .filter((line) => line.trim());

        // Collapse <tag>text</tag> onto a single line
        const collapsed: string[] = [];
        for (let i = 0; i < lines.length; i++) {
            const cur = lines[i];
            const next = lines[i + 1];
            const afterNext = lines[i + 2];
            if (
                cur?.match(/^<[^/?][^>]*[^/]>$/) &&
                next?.trim().match(/^[^<].*<\/[^>]+>$/)
            ) {
                // <Tag>\ntext</Tag>  (text and closing tag on same line)
                collapsed.push(cur + next.trim());
                i += 1;
            } else if (
                cur?.match(/^<[^/?][^>]*[^/]>$/) &&
                next && !next.trim().startsWith('<') &&
                afterNext?.match(/^<\//)
            ) {
                // <Tag>\ntext\n</Tag>  (three separate lines)
                collapsed.push(cur + next.trim() + afterNext.trim());
                i += 2;
            } else {
                collapsed.push(cur);
            }
        }

        let indent = 0;
        return collapsed
            .map((line) => {
                if (line.match(/^<\/[^>]+>/) || line.match(/<\/[^>]+>$/)) {
                    if (!line.match(/^<[^/]/)) indent--;
                }
                const result = '  '.repeat(Math.max(0, indent)) + line.trim();
                if (line.match(/^<[^/?][^>]*[^/]>$/) && !line.match(/^<[^>]+\/>/)) indent++;
                return result;
            })
            .join('\n');
    }
}
